import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { track } from "../lib/analytics";
import { applySessionToken, clearSessionToken } from "../lib/authSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        // Skip /me when returning from OAuth (AuthCallback will handle it)
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            applySessionToken(data);
            setUser({ id: data.id, name: data.name, email: data.email, role: data.role });
            track("login", { method: "email" });
            if (data.role === "admin") track("admin_login");
            return { ok: true, role: data.role };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) };
        }
    };

    const signup = async (name, email, password) => {
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            applySessionToken(data);
            setUser({ id: data.id, name: data.name, email: data.email, role: data.role });
            track("sign_up", { method: "email" });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        clearSessionToken();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
